import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import '../styles/admin-common.css';

interface User {
  user_id: string;
  email: string;
  name: string;
  is_admin: boolean;
  paid_plan?: number;
  total_exams_taken: number;
  average_score: number;
  last_login: string;
  profile_picture?: string;
  created_at: string;
}

const USERS_PER_PAGE = 10;

const UserManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [tempAdminStatus, setTempAdminStatus] = useState(false);
  const [tempPaidPlan, setTempPaidPlan] = useState(0);
  const [userSearchEmail, setUserSearchEmail] = useState('');
  const [userSearchName, setUserSearchName] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userPaidPlanFilter, setUserPaidPlanFilter] = useState('all');
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalUsers / USERS_PER_PAGE)), [totalUsers]);

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    return fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });
  };

  const getPlanLabel = (paidPlan?: number) => {
    if (paidPlan === 2) return 'AI Power';
    if (paidPlan === 1) return '프리미엄';
    return '무료';
  };

  const getPlanBadgeClass = (paidPlan?: number) => {
    if (paidPlan === 2) return 'info';
    if (paidPlan === 1) return 'success';
    return 'secondary';
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const loadUsers = async (
    searchEmail = userSearchEmail,
    searchName = userSearchName,
    role = userRoleFilter,
    paidPlan = userPaidPlanFilter,
    page = userCurrentPage
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * USERS_PER_PAGE;
      const params = new URLSearchParams({
        limit: String(USERS_PER_PAGE),
        offset: String(offset),
      });

      if (searchEmail.trim()) {
        params.set('email', searchEmail.trim());
      } else if (searchName.trim()) {
        params.set('name', searchName.trim());
      } else {
        if (role !== 'all') params.set('role', role);
        if (paidPlan !== 'all') params.set('paid_plan', paidPlan);
      }

      const response = await apiCall(`/admin/users?${params.toString()}`);
      if (response.ok) {
        const result = await response.json();
        setUsers(result.data.users || []);
        setTotalUsers(result.data.pagination.total || 0);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    setUserCurrentPage(1);
    await loadUsers(userSearchEmail, userSearchName, userRoleFilter, userPaidPlanFilter, 1);
  };

  const resetUserSearch = async () => {
    setUserSearchEmail('');
    setUserSearchName('');
    setUserRoleFilter('all');
    setUserPaidPlanFilter('all');
    setUserCurrentPage(1);
    await loadUsers('', '', 'all', 'all', 1);
  };

  const applyFilters = async () => {
    setUserCurrentPage(1);
    await loadUsers('', '', userRoleFilter, userPaidPlanFilter, 1);
  };

  const updateUserAdmin = async (userId: string, isAdmin: boolean) => {
    const response = await apiCall(`/admin/users/${userId}/admin`, {
      method: 'PUT',
      body: JSON.stringify({ isAdmin }),
    });

    if (response.ok) {
      await loadUsers();
      if (userId === user?.user_id) {
        window.location.reload();
      }
    }
  };

  const updateUserPlan = async (userId: string, paidPlan: number) => {
    const response = await apiCall(`/admin/users/${userId}/plan`, {
      method: 'PUT',
      body: JSON.stringify({ paidPlan }),
    });

    if (response.ok) {
      await loadUsers();
    }
  };

  const openUserModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    setTempAdminStatus(targetUser.is_admin);
    setTempPaidPlan(targetUser.paid_plan || 0);
  };

  const saveUserChanges = async () => {
    if (!selectedUser) return;

    if (tempAdminStatus !== selectedUser.is_admin) {
      await updateUserAdmin(selectedUser.user_id, tempAdminStatus);
    }

    if (tempPaidPlan !== selectedUser.paid_plan) {
      await updateUserPlan(selectedUser.user_id, tempPaidPlan);
    }

    setSelectedUser(null);
  };

  useEffect(() => {
    loadUsers(userSearchEmail, userSearchName, userRoleFilter, userPaidPlanFilter, userCurrentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCurrentPage]);

  if (!user?.is_admin) {
    return (
      <div className="admin-page">
        <div className="access-denied">
          <h2>접근 권한이 없습니다</h2>
          <p>관리자 권한이 필요한 페이지입니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-page--users">
      <header className="admin-page-title">
        <div>
          <span className="admin-page-title__eyebrow">관리자 워크스페이스</span>
          <h1>사용자 관리</h1>
          <p>계정 권한, 플랜, 최근 로그인, 시험 이용 현황을 한 곳에서 확인합니다.</p>
        </div>
      </header>

      <section className="admin-section user-management-panel">
        <div className="admin-header user-management-panel__header">
          <div>
            <h2>사용자 목록</h2>
            <p>총 {totalUsers.toLocaleString()}명의 사용자</p>
          </div>

          <div className="admin-controls user-management-controls">
            <div className="admin-form-inline user-management-controls__group">
              <select
                className="admin-form-control sm select"
                value={userRoleFilter}
                onChange={(event) => setUserRoleFilter(event.target.value)}
              >
                <option value="all">전체 사용자</option>
                <option value="admin">관리자</option>
                <option value="paid_plan">유료 플랜</option>
                <option value="user">일반 사용자</option>
              </select>
              <select
                className="admin-form-control sm select"
                value={userPaidPlanFilter}
                onChange={(event) => setUserPaidPlanFilter(event.target.value)}
                disabled={userRoleFilter === 'admin'}
              >
                <option value="all">모든 플랜</option>
                <option value="0">무료</option>
                <option value="1">프리미엄</option>
                <option value="2">AI Power</option>
              </select>
              <button className="admin-btn secondary" onClick={applyFilters}>필터 적용</button>
            </div>

            <div className="admin-form-inline user-management-controls__group">
              <input
                type="email"
                className="admin-form-control sm"
                placeholder="이메일로 검색"
                value={userSearchEmail}
                onChange={(event) => setUserSearchEmail(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && searchUsers()}
              />
              <input
                type="text"
                className="admin-form-control sm"
                placeholder="이름으로 검색"
                value={userSearchName}
                onChange={(event) => setUserSearchName(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && searchUsers()}
              />
              <button className="admin-btn secondary" onClick={searchUsers}>검색</button>
              <button className="admin-btn secondary" onClick={resetUserSearch}>초기화</button>
            </div>
          </div>
        </div>

        <div className="users-stats">
          <span>총 {totalUsers.toLocaleString()}명</span>
          <span>페이지 {userCurrentPage} / {totalPages}</span>
          {loading && <span>불러오는 중...</span>}
        </div>

        <div className="user-admin-table-wrap">
          <table className="admin-table user-admin-table">
            <thead>
              <tr>
                <th className="col-user">사용자</th>
                <th className="col-email">이메일</th>
                <th className="col-role">권한</th>
                <th className="col-plan">플랜</th>
                <th className="col-exams">시험 수</th>
                <th className="col-score">평균 점수</th>
                <th className="col-login">최근 로그인</th>
                <th className="col-action">작업</th>
              </tr>
            </thead>
            <tbody>
              {users.map((item) => (
                <tr key={item.user_id}>
                  <td className="col-user">
                    <div className="user-cell">
                      <div className="user-avatar">
                        {item.profile_picture ? (
                          <img
                            src={item.profile_picture}
                            alt={item.name || item.email}
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = '/default-avatar.svg';
                            }}
                          />
                        ) : (
                          <div className="avatar-placeholder">{item.name?.charAt(0) || 'U'}</div>
                        )}
                      </div>
                      <div className="user-cell__meta">
                        <strong>{item.name || '이름 없음'}</strong>
                        <span className="admin-text-gray-500">{item.user_id.slice(0, 12)}...</span>
                      </div>
                    </div>
                  </td>
                  <td className="col-email user-email-cell">{item.email}</td>
                  <td className="col-role">
                    <span className={`admin-badge ${item.is_admin ? 'danger' : 'secondary'}`}>
                      {item.is_admin ? '관리자' : '사용자'}
                    </span>
                  </td>
                  <td className="col-plan">
                    <span className={`admin-badge ${getPlanBadgeClass(item.paid_plan)}`}>
                      {getPlanLabel(item.paid_plan)}
                    </span>
                  </td>
                  <td className="col-exams">{item.total_exams_taken}</td>
                  <td className="col-score">{item.average_score}%</td>
                  <td className="col-login">{formatDateTime(item.last_login)}</td>
                  <td className="col-action">
                    <button className="admin-btn secondary sm" onClick={() => openUserModal(item)}>
                      수정
                    </button>
                  </td>
                </tr>
              ))}

              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={8} className="admin-empty-cell">조건에 맞는 사용자가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalUsers > USERS_PER_PAGE && (
          <div className="admin-pagination">
            <button
              className="admin-btn secondary sm"
              disabled={userCurrentPage === 1}
              onClick={() => setUserCurrentPage(userCurrentPage - 1)}
            >
              이전
            </button>
            <span>{userCurrentPage} / {totalPages}</span>
            <button
              className="admin-btn secondary sm"
              disabled={userCurrentPage >= totalPages}
              onClick={() => setUserCurrentPage(userCurrentPage + 1)}
            >
              다음
            </button>
          </div>
        )}
      </section>

      {selectedUser && (
        <div className="user-edit-modal-overlay">
          <div className="user-edit-modal">
            <div className="user-edit-modal__header">
              <h3>사용자 정보 수정</h3>
              <button className="user-edit-modal__close" onClick={() => setSelectedUser(null)} aria-label="닫기">
                닫기
              </button>
            </div>
            <div className="user-edit-modal__body">
              <div className="user-edit-profile">
                <div className="user-avatar large">
                  {selectedUser.profile_picture ? (
                    <img
                      src={selectedUser.profile_picture}
                      alt={selectedUser.name || selectedUser.email}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = '/default-avatar.svg';
                      }}
                    />
                  ) : (
                    <div className="avatar-placeholder">{selectedUser.name?.charAt(0) || 'U'}</div>
                  )}
                </div>
                <div className="user-edit-profile__meta">
                  <h4>{selectedUser.name || '이름 없음'}</h4>
                  <p>{selectedUser.email}</p>
                  <div className="user-edit-profile__stats">
                    <span>시험 {selectedUser.total_exams_taken}회</span>
                    <span>평균 {selectedUser.average_score}%</span>
                    <span>가입일 {new Date(selectedUser.created_at).toLocaleDateString('ko-KR')}</span>
                  </div>
                </div>
              </div>

              <div className="user-edit-form">
                <div className="user-edit-form__row">
                  <label className="user-edit-form__label">권한</label>
                  <label className="user-edit-form__checkbox">
                    <input
                      type="checkbox"
                      checked={tempAdminStatus}
                      onChange={(event) => setTempAdminStatus(event.target.checked)}
                    />
                    관리자 권한 부여
                  </label>
                </div>

                <div className="user-edit-form__row">
                  <label className="user-edit-form__label">유료 플랜</label>
                  <select
                    className="admin-form-control"
                    value={tempPaidPlan}
                    onChange={(event) => setTempPaidPlan(Number(event.target.value))}
                  >
                    <option value={0}>무료</option>
                    <option value={1}>프리미엄</option>
                    <option value={2}>AI Power</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="user-edit-modal__footer">
              <button className="admin-btn primary" onClick={saveUserChanges}>저장</button>
              <button className="admin-btn secondary" onClick={() => setSelectedUser(null)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
